import apiErrorHandler from '@/libraries/apiErrorHandler';
import FbaApiError from '@/types/FbaApiError';
import {
    VirtualRedTagNotificationPreferenceRequest,
    VirtualRedTagNotificationPreferenceResponse,
} from '@/types/VirtualRedTagNotification';
import axios from 'axios';

// Without a timeout axios waits forever, which would leave the form locked on a stalled
// request. On expiry the error handler reports a timeout and the driver can retry.
const SAVE_TIMEOUT_MS = 20000;

// This endpoint answers every rejection (and a 401) with
// { isSuccessful: false, validationErrors: [{ field, message }] }, not the
// { message } body apiErrorHandler reads.
const firstValidationError = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any
): { message?: string; field?: string } => {
    const first = error?.response?.data?.validationErrors?.[0];
    const text = (value: unknown): string | undefined =>
        typeof value === 'string' && value.trim().length > 0
            ? value.trim()
            : undefined;
    return { message: text(first?.message), field: text(first?.field) };
};

// Resolves to the saved result on success. The contact value is personal data: it
// only goes in the request body, never into a log.
export default async function postVirtualRedTagNotificationPreference(
    dto: VirtualRedTagNotificationPreferenceRequest
): Promise<VirtualRedTagNotificationPreferenceResponse | FbaApiError> {
    try {
        const response = await axios({
            url: `${process.env.VUE_APP_EXT_API_URL}api/virtualRedTag/notificationPreference`,
            method: 'POST',
            // The API checks the Eleos session cookie and that the caller is the
            // driver who submitted the red tag.
            withCredentials: true,
            timeout: SAVE_TIMEOUT_MS,
            data: {
                assessmentId: dto.assessmentId,
                notificationMethod: dto.notificationMethod,
                contactValue: dto.contactValue,
            },
        });

        return {
            trailerAlreadyInShop: response.data?.trailerAlreadyInShop === true,
        };
    } catch (error) {
        const handled = await apiErrorHandler(error);
        const { message, field } = firstValidationError(error);
        if (message) {
            handled.message = message;
        }
        handled.field = field;
        return handled;
    }
}
